import { useCallback, useEffect, useRef } from "react"

/**
 * Hook that throttles a function call, ensuring it's only executed once per specified interval
 * @param callback - The function to throttle
 * @param delay - The throttle delay in milliseconds (default: 100ms)
 * @returns A throttled version of the callback function
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useThrottle<TFunction extends (...args: any[]) => any>(
  callback: TFunction,
  delay = 100
): (...args: Parameters<TFunction>) => void {
  const lastExecuted = useRef<number>(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const throttledCallback = useCallback(
    (...args: Parameters<TFunction>) => {
      const now = Date.now()
      const timeSinceLastExecution = now - lastExecuted.current

      if (timeSinceLastExecution >= delay) {
        // Execute immediately if enough time has passed
        lastExecuted.current = now
        callback(...args)
      } else {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
          lastExecuted.current = Date.now()
          callback(...args)
        }, delay - timeSinceLastExecution)
      }
    },
    [callback, delay]
  ) as (...args: Parameters<TFunction>) => void

  return throttledCallback
}
